import NetInfo from '@react-native-community/netinfo';
import { Platform } from 'react-native';
import { api } from './api';
import {
  applyDetoxNetworkOverride,
  subscribeToDetoxNetworkOverride,
} from '../utils/detoxTestControls';
import { shouldUseOfflineReviewWorkflow } from '../config/offlineWorkflow';

// Platform-specific storage
const getStorage = () => {
  if (Platform.OS === 'web') {
    // Use localStorage for web
    return {
      getItem: async (key: string) => localStorage.getItem(key),
      setItem: async (key: string, value: string) => localStorage.setItem(key, value),
      removeItem: async (key: string) => localStorage.removeItem(key),
      clear: async () => localStorage.clear(),
    };
  } else {
    // Use storage for mobile
    const storage = require('@react-native-async-storage/async-storage').default;
    return storage;
  }
};

const storage = getStorage();
import { store } from '../store';
import {
  setOnlineStatus,
  addPendingAction,
  removePendingAction,
  incrementRetryCount,
  setSyncInProgress,
  setLastSyncTime,
  clearPendingActions,
  startSyncProgress,
  updateSyncProgress,
  completeSyncProgress,
  addSyncError,
  clearSyncProgress,
} from '../store/slices/offlineSlice';

export interface OfflineAction {
  id: string;
  type: string;
  payload: any;
  timestamp: number;
  retryCount: number;
  endpoint?: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | string;
  priority?: number; // 1 = highest (responses), 2 = medium (photos), 3 = lowest (metadata)
  estimatedDuration?: number; // in milliseconds
  size?: number; // data size for bandwidth optimization
}

export interface SyncProgressCallback {
  (progress: {
    percentage: number;
    currentOperation: string;
    itemsRemaining: number;
    estimatedTimeRemaining?: number;
    currentItem?: number;
    totalItems?: number;
    queueVisualization?: QueueItem[];
  }): void;
}

export interface QueueItem {
  id: string;
  type: string;
  priority: number;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  retryCount: number;
  estimatedDuration?: number;
  errorMessage?: string;
}

export interface SyncOptions {
  priority?: number;
  batchSize?: number;
  onProgress?: SyncProgressCallback;
  resumeFromInterruption?: boolean;
  maxConcurrent?: number;
}

class SyncService {
  private syncInterval: NodeJS.Timeout | null = null;
  private readonly MAX_RETRIES = 3;
  private readonly SYNC_INTERVAL = 60000; // 60 seconds
  private readonly STORAGE_KEY = 'offline_actions';
  private readonly SYNC_HISTORY_KEY = 'sync_history';

  // Progress tracking
  private currentSyncId: string | null = null;
  private syncStartTime: number = 0;
  private syncAbortController: AbortController | null = null;
  private progressCallbacks: Map<string, SyncProgressCallback> = new Map();

  // Priority weights for sync order
  private readonly PRIORITY_WEIGHTS = {
    'UPDATE_RESPONSE': 1,     // Highest priority - user responses
    'SUBMIT_REVIEW': 2,       // Medium priority - review submission (after responses)
    'COMPLETE_REVIEW': 2,     // Medium priority - review completion (same as submit)
    'CREATE_REVIEW': 2,       // Medium priority - review creation
    'UPLOAD_PHOTO': 2,        // Medium priority - photos
    'DELETE_PHOTO': 3,        // Lower priority - cleanup
  };

  // Estimated durations (ms) for different operation types
  private readonly ESTIMATED_DURATIONS = {
    'UPDATE_RESPONSE': 1000,
    'SUBMIT_REVIEW': 2000,
    'COMPLETE_REVIEW': 2000,  // Similar to submit
    'CREATE_REVIEW': 1500,
    'UPLOAD_PHOTO': 5000,     // Photos take longer
    'DELETE_PHOTO': 500,
  };

  // Batch configuration
  private readonly BATCH_SIZES = {
    'UPDATE_RESPONSE': 10,    // Batch responses together
    'UPLOAD_PHOTO': 3,        // Limit concurrent photo uploads
    'DELETE_PHOTO': 20,       // Batch deletes
    'DEFAULT': 5,
  };

  private syncHistory: Array<{
    id: string;
    timestamp: number;
    duration: number;
    itemsSynced: number;
    errors: string[];
    success: boolean;
  }> = [];

  async initialize() {
    // Listen to network state changes
    NetInfo.addEventListener(state => {
      const isOnline = applyDetoxNetworkOverride(state);
      store.dispatch(setOnlineStatus(isOnline));

      if (isOnline) {
        this.startSync();
      } else {
        this.stopSync();
      }
    });

    subscribeToDetoxNetworkOverride((isOnline) => {
      store.dispatch(setOnlineStatus(isOnline));

      if (isOnline) {
        this.startSync();
      } else {
        this.stopSync();
      }
    });

    // Load pending actions and sync history from storage
    await this.loadPendingActions();
    await this.loadSyncHistory();

    // Check initial network state
    const netState = await NetInfo.fetch();
    const isOnline = applyDetoxNetworkOverride(netState);
    store.dispatch(setOnlineStatus(isOnline));

    if (isOnline) {
      this.startSync();
    }
  }

  private async loadPendingActions() {
    try {
      const stored = await storage.getItem(this.STORAGE_KEY);
      if (stored) {
        const actions = JSON.parse(stored);
        actions.forEach((action: OfflineAction) => {
          store.dispatch(addPendingAction({
            type: action.type,
            payload: action.payload,
            endpoint: action.endpoint,
            method: action.method,
          }));
        });
      }
    } catch (error) {
      console.warn('[SyncService] Failed to load pending actions:', error);
    }
  }

  private async savePendingActions() {
    try {
      const state = store.getState();
      await storage.setItem(
        this.STORAGE_KEY,
        JSON.stringify(state.offline.pendingActions)
      );
    } catch (error) {
      console.warn('[SyncService] Failed to save pending actions:', error);
    }
  }

  async addAction(action: Omit<OfflineAction, 'id' | 'timestamp' | 'retryCount' | 'priority' | 'estimatedDuration' | 'size'>) {
    const state = store.getState();

    // Enhance action with priority and metadata
    const enhancedAction = {
      ...action,
      priority: this.PRIORITY_WEIGHTS[action.type] || 3,
      estimatedDuration: this.ESTIMATED_DURATIONS[action.type] || 2000,
      size: this.estimateActionSize(action),
    };

    if (state.offline.isOnline && !shouldUseOfflineReviewWorkflow()) {
      // Execute immediately if online
      try {
        await this.executeAction(enhancedAction);
        return;
      } catch (error) {
        console.warn('[SyncService] Failed to execute action immediately:', error);
        // Fall through to queue the action
      }
    }

    // Queue for later if offline or immediate execution failed
    store.dispatch(addPendingAction(enhancedAction));
    await this.savePendingActions();
  }

  private estimateActionSize(action: any): number {
    try {
      const serialized = JSON.stringify(action.payload);
      return serialized.length;
    } catch {
      return 1000; // Default size estimate
    }
  }

  private async executeAction(action: Omit<OfflineAction, 'id' | 'timestamp' | 'retryCount'>): Promise<any> {
    // Handle special action types that need custom API methods
    if (action.type === 'UPLOAD_PHOTO') {
      const { photos } = action.payload;
      return api.uploadFiles<unknown>(action.endpoint, photos);
    }

    switch (action.method) {
      case 'GET':
        return api.get<unknown>(action.endpoint);
      case 'POST':
        return api.post<unknown>(action.endpoint, action.payload);
      case 'PUT':
        return api.put<unknown>(action.endpoint, action.payload);
      case 'DELETE':
        return api.delete<unknown>(action.endpoint);
      default:
        throw new Error(`Unsupported method: ${action.method}`);
    }
  }

  private startSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }

    // Immediate sync
    this.performSync();

    // Periodic sync
    this.syncInterval = setInterval(() => {
      this.performSync();
    }, this.SYNC_INTERVAL);
  }

  private stopSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  private async performSync(options: SyncOptions = {}) {
    const state = store.getState();

    if (!state.offline.isOnline || state.offline.syncInProgress || state.offline.pendingActions.length === 0) {
      return;
    }

    const syncId = `sync_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    this.currentSyncId = syncId;
    this.syncStartTime = Date.now();
    this.syncAbortController = new AbortController();

    console.warn('[SyncService] Starting sync operation', { syncId, pendingActions: state.offline.pendingActions.length });

    store.dispatch(setSyncInProgress(true));

    // Sort actions by priority and timestamp for optimal sync order
    const sortedActions = [...state.offline.pendingActions].sort((a, b) => {
      const aPriority = a.priority ?? this.PRIORITY_WEIGHTS[a.type] ?? 3;
      const bPriority = b.priority ?? this.PRIORITY_WEIGHTS[b.type] ?? 3;
      if (aPriority !== bPriority) {
        return aPriority - bPriority; // Lower number = higher priority
      }
      return a.timestamp - b.timestamp; // Older first within same priority
    });

    // Initialize progress tracking
    store.dispatch(startSyncProgress({ totalOperations: sortedActions.length }));

    const successfulActions: string[] = [];
    const failedActions: string[] = [];
    const syncErrors: string[] = [];
    let completedCount = 0;

    try {
      // Process actions in batches based on type
      const batches = this.createSyncBatches(sortedActions, options);

      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];

        // Check if sync was cancelled
        if (this.syncAbortController?.signal.aborted) {
          console.warn('[SyncService] Sync operation cancelled');
          break;
        }

        // Update progress with current batch info
        const remainingItems = sortedActions.length - completedCount;
        const estimatedTimeRemaining = this.calculateEstimatedTime(remainingItems, sortedActions.slice(completedCount));

        store.dispatch(updateSyncProgress({
          currentOperation: `Processing batch ${batchIndex + 1}/${batches.length} (${batch.length} items)`,
          completedOperations: completedCount,
        }));

        // Notify progress callbacks
        this.notifyProgressCallbacks({
          percentage: (completedCount / sortedActions.length) * 100,
          currentOperation: `Syncing ${batch[0]?.type || 'items'} (${completedCount + 1}-${Math.min(completedCount + batch.length, sortedActions.length)} of ${sortedActions.length})`,
          itemsRemaining: remainingItems,
          estimatedTimeRemaining,
          currentItem: completedCount + 1,
          totalItems: sortedActions.length,
          queueVisualization: this.createQueueVisualization(sortedActions, completedCount),
        });

        // Process batch with bandwidth optimization
        const batchResults = await this.processBatch(batch, options);

        batchResults.forEach((result, index) => {
          const action = batch[index];
          if (result.success) {
            successfulActions.push(action.id);
          } else {
            if (action.retryCount < this.MAX_RETRIES) {
              store.dispatch(incrementRetryCount(action.id));
              failedActions.push(action.id);
            } else {
              // Max retries reached, remove action but log error
              successfulActions.push(action.id);
              syncErrors.push(`Max retries exceeded for ${action.type}: ${result.error}`);
            }

            if (result.error) {
              syncErrors.push(`${action.type}: ${result.error}`);
            }
          }
        });

        completedCount += batch.length;
      }

      // Remove successful actions
      successfulActions.forEach(actionId => {
        store.dispatch(removePendingAction(actionId));
      });

      // Update storage
      await this.savePendingActions();

      // Update last sync time
      store.dispatch(setLastSyncTime(Date.now()));

      // Record sync history
      await this.recordSyncHistory({
        id: syncId,
        timestamp: this.syncStartTime,
        duration: Date.now() - this.syncStartTime,
        itemsSynced: successfulActions.length,
        errors: syncErrors,
        success: syncErrors.length === 0,
      });

      // Final progress update
      store.dispatch(completeSyncProgress());

      this.notifyProgressCallbacks({
        percentage: 100,
        currentOperation: `Sync completed: ${successfulActions.length} items synced`,
        itemsRemaining: 0,
        estimatedTimeRemaining: 0,
        currentItem: sortedActions.length,
        totalItems: sortedActions.length,
      });

      console.warn('[SyncService] Sync completed', {
        syncId,
        duration: Date.now() - this.syncStartTime,
        successful: successfulActions.length,
        failed: failedActions.length,
        errors: syncErrors.length,
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown sync error';
      console.warn('[SyncService] Sync failed:', error);
      store.dispatch(addSyncError(errorMessage));

      await this.recordSyncHistory({
        id: syncId,
        timestamp: this.syncStartTime,
        duration: Date.now() - this.syncStartTime,
        itemsSynced: successfulActions.length,
        errors: [errorMessage, ...syncErrors],
        success: false,
      });
    } finally {
      store.dispatch(setSyncInProgress(false));
      this.currentSyncId = null;
      this.syncAbortController = null;
    }
  }

  // Legacy method name for backward compatibility
  async forcSync(options: SyncOptions = {}) {
    const state = store.getState();
    let recoveryResult;
    if (state.offline.isOnline) {
      if (shouldUseOfflineReviewWorkflow()) {
        const { getOfflineReviewService } = require('./platformServices');
        const offlineReviewService = getOfflineReviewService();
        recoveryResult = offlineReviewService.retryRecoverySync
          ? await offlineReviewService.retryRecoverySync()
          : await offlineReviewService.performFullSync?.();
        store.dispatch(setLastSyncTime(Date.now()));
      }

      await this.performSync(options);
    }
    return recoveryResult;
  }

  // New method name with better naming
  async forceSync(options: SyncOptions = {}) {
    return this.forcSync(options);
  }

  // Cancel ongoing sync operation
  cancelSync() {
    if (this.syncAbortController) {
      console.warn('[SyncService] Cancelling sync operation');
      this.syncAbortController.abort();
      store.dispatch(addSyncError('Sync cancelled by user'));
    }
  }

  async clearOfflineData() {
    store.dispatch(clearPendingActions());
    await storage.removeItem(this.STORAGE_KEY);
  }

  // Helper methods for specific action types
  async queueReviewUpdate(reviewId: string, responseId: string, data: any) {
    await this.addAction({
      type: 'UPDATE_RESPONSE',
      endpoint: `/reviews/${reviewId}/responses/${responseId}`,
      method: 'PUT',
      payload: data,
    });
  }

  async queueReviewSubmission(reviewId: string) {
    await this.addAction({
      type: 'SUBMIT_REVIEW',
      endpoint: `/reviews/${reviewId}/submit`,
      method: 'POST',
      payload: {},
    });
  }

  async queueReviewCompletion(reviewId: string) {
    await this.addAction({
      type: 'COMPLETE_REVIEW',
      endpoint: `/reviews/${reviewId}/complete`,
      method: 'POST',
      payload: {},
    });
  }

  async queuePhotoUpload(reviewId: string, responseId: string, itemId: string, photos: any[], questionId?: string) {
    // Build endpoint based on whether questionId is provided
    const endpoint = questionId
      ? `/upload/reviews/${reviewId}/responses/${responseId}/items/${itemId}/questions/${questionId}/photos`
      : `/upload/reviews/${reviewId}/responses/${responseId}/items/${itemId}/photos`;

    await this.addAction({
      type: 'UPLOAD_PHOTO',
      endpoint,
      method: 'POST',
      payload: { photos, questionId },
    });
  }

  async queuePhotoDelete(photoId: string) {
    await this.addAction({
      type: 'DELETE_PHOTO',
      endpoint: `/upload/photos/${photoId}`,
      method: 'DELETE',
      payload: {},
    });
  }

  async queueReviewCreation(data: any) {
    await this.addAction({
      type: 'CREATE_REVIEW',
      endpoint: '/reviews',
      method: 'POST',
      payload: data,
    });
  }

  // Enhanced queue methods with progress support
  async queueMultipleActions(actions: Array<Omit<OfflineAction, 'id' | 'timestamp' | 'retryCount' | 'priority' | 'estimatedDuration' | 'size'>>, options?: SyncOptions) {
    for (const action of actions) {
      await this.addAction(action);
    }

    // If online, optionally trigger immediate sync with progress
    const state = store.getState();
    if (state.offline.isOnline && options?.onProgress) {
      await this.forceSync(options);
    }
  }

  // Get comprehensive sync status information
  getSyncStatus() {
    const state = store.getState();
    return {
      isOnline: state.offline.isOnline,
      pendingActionsCount: state.offline.pendingActions.length,
      syncInProgress: state.offline.syncInProgress,
      lastSyncTime: state.offline.lastSyncTime,
      syncProgress: state.offline.syncProgress,
      queueVisualization: this.createQueueVisualization(state.offline.pendingActions, 0),
      syncHistory: this.syncHistory,
      currentSyncId: this.currentSyncId,
    };
  }

  // Progress tracking methods
  onProgress(callbackId: string, callback: SyncProgressCallback) {
    this.progressCallbacks.set(callbackId, callback);
  }

  offProgress(callbackId: string) {
    this.progressCallbacks.delete(callbackId);
  }

  private notifyProgressCallbacks(progress: Parameters<SyncProgressCallback>[0]) {
    this.progressCallbacks.forEach(callback => {
      try {
        callback(progress);
      } catch (error) {
        console.warn('[SyncService] Progress callback error:', error);
      }
    });
  }

  private createQueueVisualization(actions: OfflineAction[], completedCount: number): QueueItem[] {
    return actions.map((action, index) => ({
      id: action.id,
      type: action.type,
      priority: action.priority || this.PRIORITY_WEIGHTS[action.type] || 3,
      status: index < completedCount ? 'completed' : 'pending',
      retryCount: action.retryCount,
      estimatedDuration: action.estimatedDuration || this.ESTIMATED_DURATIONS[action.type] || 2000,
      errorMessage: action.retryCount > 0 ? `Retry attempt ${action.retryCount}` : undefined,
    }));
  }

  private calculateEstimatedTime(remainingItems: number, remainingActions: OfflineAction[]): number {
    if (remainingItems === 0) return 0;

    const totalEstimatedDuration = remainingActions.reduce((total, action) => {
      return total + (action.estimatedDuration || this.ESTIMATED_DURATIONS[action.type] || 2000);
    }, 0);

    // Add some buffer time for network latency and processing
    return Math.ceil(totalEstimatedDuration * 1.2);
  }

  private createSyncBatches(actions: OfflineAction[], options: SyncOptions): OfflineAction[][] {
    const batches: OfflineAction[][] = [];
    const actionsByType = new Map<string, OfflineAction[]>();

    // Group actions by type
    actions.forEach(action => {
      if (!actionsByType.has(action.type)) {
        actionsByType.set(action.type, []);
      }
      actionsByType.get(action.type)!.push(action);
    });

    // Create batches based on type and batch size
    actionsByType.forEach((typeActions, type) => {
      const batchSize = options.batchSize || this.BATCH_SIZES[type] || this.BATCH_SIZES.DEFAULT;

      for (let i = 0; i < typeActions.length; i += batchSize) {
        batches.push(typeActions.slice(i, i + batchSize));
      }
    });

    // Sort batches by priority (lower number = higher priority)
    return batches.sort((a, b) => {
      const aPriority = Math.min(...a.map(action => action.priority || 3));
      const bPriority = Math.min(...b.map(action => action.priority || 3));
      return aPriority - bPriority;
    });
  }

  private async processBatch(batch: OfflineAction[], options: SyncOptions): Promise<Array<{success: boolean; error?: string}>> {
    const results = [];
    const maxConcurrent = options.maxConcurrent || Math.min(3, batch.length);

    // Process batch items with controlled concurrency
    for (let i = 0; i < batch.length; i += maxConcurrent) {
      const chunk = batch.slice(i, i + maxConcurrent);
      const chunkPromises = chunk.map(async (action) => {
        try {
          await this.executeAction(action);
          return { success: true };
        } catch (error) {
          return {
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error'
          };
        }
      });

      const chunkResults = await Promise.all(chunkPromises);
      results.push(...chunkResults);
    }

    return results;
  }

  private async recordSyncHistory(record: {
    id: string;
    timestamp: number;
    duration: number;
    itemsSynced: number;
    errors: string[];
    success: boolean;
  }) {
    this.syncHistory.unshift(record);

    // Keep only last 50 sync records
    if (this.syncHistory.length > 50) {
      this.syncHistory = this.syncHistory.slice(0, 50);
    }

    try {
      await storage.setItem(this.SYNC_HISTORY_KEY, JSON.stringify(this.syncHistory));
    } catch (error) {
      console.warn('[SyncService] Failed to save sync history:', error);
    }
  }

  private async loadSyncHistory() {
    try {
      const stored = await storage.getItem(this.SYNC_HISTORY_KEY);
      if (stored) {
        this.syncHistory = JSON.parse(stored);
      }
    } catch (error) {
      console.warn('[SyncService] Failed to load sync history:', error);
      this.syncHistory = [];
    }
  }

  // Get sync statistics
  getSyncStatistics() {
    const totalSyncs = this.syncHistory.length;
    const successfulSyncs = this.syncHistory.filter(record => record.success).length;
    const averageDuration = totalSyncs > 0
      ? this.syncHistory.reduce((sum, record) => sum + record.duration, 0) / totalSyncs
      : 0;
    const totalItemsSynced = this.syncHistory.reduce((sum, record) => sum + record.itemsSynced, 0);

    return {
      totalSyncs,
      successfulSyncs,
      successRate: totalSyncs > 0 ? (successfulSyncs / totalSyncs) * 100 : 0,
      averageDuration: Math.round(averageDuration),
      totalItemsSynced,
      recentErrors: this.syncHistory.slice(0, 5).flatMap(record => record.errors),
    };
  }
}

export const syncService = new SyncService();
